import React, { useCallback, useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClockRotateLeft, faFileArrowDown, faMoneyBillTransfer, faRotate, faShieldHalved, faXmark } from "@fortawesome/free-solid-svg-icons";
import { fluxopayApi } from "../../services/fluxopayApi";
import "./FluxopayPayoutPanel.css";
import "./FluxopayPayoutPanelSecure.css";
import PayoutAuditTimeline from "./PayoutAuditTimeline";
import FluxopayPayoutGovernancePanel from "./FluxopayPayoutGovernancePanel";

import {fluxopayStatusLabel,} from "./fluxopayStatus";

const money = (cents) => Number(cents || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const dateTime = (value) => value ? new Date(value).toLocaleString("pt-BR") : "—";
const newIdempotencyKey = () => window.crypto?.randomUUID?.() || `${Date.now()}-0000-4000-8000-${Math.random().toString(16).slice(2).padEnd(12, "0").slice(0, 12)}`;

export default function FluxopayPayoutPanelSecure() {
  const [balance, setBalance] = useState(0);
  const [risk, setRisk] = useState({
    payoutsBlocked: false,
    negativeBalanceCents: 0,
    reserveCents: 0,
    providerAvailableCents: 0,
  });
  const [destination, setDestination] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [modal, setModal] = useState(null);
  const [pixKey, setPixKey] = useState("");
  const [amount, setAmount] = useState("");
  const [password, setPassword] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [idempotencyKey, setIdempotencyKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [busyRow, setBusyRow] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [auditPayout, setAuditPayout] = useState(null);

  const load = useCallback(async () => {
    const [overview, destinationResponse, payoutResponse] = await Promise.all([
      fluxopayApi.overview(), fluxopayApi.payoutDestination(), fluxopayApi.payouts({ per_page: 25 }),
    ]);
    setBalance(Number(overview.balances?.available_cents || 0));
    setRisk({
      payoutsBlocked: Boolean(
        overview.balances?.payouts_blocked
      ),

      negativeBalanceCents: Number(
        overview.balances?.negative_balance_cents || 0
      ),

      reserveCents: Number(
        overview.balances?.refund_reserve_cents || 0
      ),

      providerAvailableCents: Number(
        overview.balances?.provider_available_cents
          ?? overview.balances?.available_cents
          ?? 0
      ),
    });
    setDestination(destinationResponse.destination || null);
    setPayouts(payoutResponse.data || []);
  }, []);

  useEffect(() => { load().catch((requestError) => setError(requestError.response?.data?.message || "Não foi possível carregar os repasses.")); }, [load]);

  const openModal = (type) => {
    setModal(type); setError(""); setNotice(""); setPassword(""); setConfirmed(false);
    if (type === "payout") setIdempotencyKey(newIdempotencyKey());
  };
  const closeModal = () => { if (!busy) { setModal(null); setPassword(""); } };
  const message = (requestError, fallback) => {
    const providerErrors = requestError.response?.data?.errors;
    if (Array.isArray(providerErrors)) return providerErrors.map((item) => item.description).filter(Boolean).join(" ") || fallback;
    return requestError.response?.data?.message || Object.values(requestError.response?.data?.errors || {}).flat().join(" ") || fallback;
  };

  const saveDestination = async (event) => {
    event.preventDefault(); setBusy(true); setError("");
    try {
      await fluxopayApi.savePayoutDestination({ pix_key: pixKey, password, confirm_ownership: true });
      setPixKey(""); setModal(null); setNotice("Chave Pix atualizada com segurança."); await load();
    } catch (requestError) { setError(message(requestError, "Não foi possível cadastrar a chave Pix.")); }
    finally { setBusy(false); }
  };

  const requestPayout = async (event) => {
    event.preventDefault(); setBusy(true); setError("");
    try {
      await fluxopayApi.requestPayout({ amount_cents: Math.round(Number(String(amount).replace(",", ".")) * 100), idempotency_key: idempotencyKey, password, confirm: confirmed });
      setAmount(""); setPassword(""); setConfirmed(false); setModal(null); setNotice("Solicitação registrada. O status será atualizado pelo Asaas."); await load();
    } catch (requestError) { setError(message(requestError, "Não foi possível solicitar a transferência.")); }
    finally { setBusy(false); }
  };

  const sync = async (row) => {
    setBusyRow(row.uuid); setError("");
    try { await fluxopayApi.syncPayout(row.uuid); setNotice("Status do repasse atualizado."); await load(); }
    catch (requestError) { setError(message(requestError, "Não foi possível sincronizar o repasse.")); }
    finally { setBusyRow(null); }
  };
  const receipt = async (row) => {
    setBusyRow(row.uuid); setError("");
    try { const response = await fluxopayApi.payoutReceipt(row.uuid); window.open(response.receipt_url, "_blank", "noopener,noreferrer"); }
    catch (requestError) { setError(message(requestError, "O comprovante ainda não está disponível.")); }
    finally { setBusyRow(null); }
  };
  const reconcile = async () => {
    setBusy(true); setError("");
    try { const response = await fluxopayApi.reconcilePayouts(); setNotice(`${response.message} ${response.result?.matched || 0} conciliado(s), ${response.result?.divergent || 0} divergente(s).`); await load(); }
    catch (requestError) { setError(message(requestError, "Não foi possível conciliar os repasses.")); }
    finally { setBusy(false); }
  };

  return <div className="fxpo-wrap">
    {error && <div className="fxpo-error">{error}</div>}
    {notice && <div className="fxpo-success">{notice}</div>}
    {risk.payoutsBlocked && (
      <div className="fxpo-error">
        <strong>Repasses temporariamente bloqueados.</strong>

        <span>
          Existem {money(risk.negativeBalanceCents / 100)}
          {" "}em estornos ainda não compensados. Os próximos
          recebimentos reduzirão automaticamente esse saldo.
        </span>
      </div>
    )}
    <FluxopayPayoutGovernancePanel
      onChanged={load}
    />

    <section className="fxpay-balance-banner fxpo-balance">
      <div><small>SALDO LIBERADO PARA REPASSE</small><strong>{money(balance / 100)}</strong><p>{destination ? `Destino principal: ${destination.display_mask}` : "Cadastre uma chave Pix CNPJ para solicitar repasses."}</p></div>
      <div className="fxpo-actions"><button className="fxpo-secondary" onClick={() => openModal("destination")}><FontAwesomeIcon icon={faShieldHalved} /> {destination ? "Alterar chave Pix" : "Cadastrar chave Pix"}</button><button className="fxpo-secondary" disabled={busy} onClick={reconcile}><FontAwesomeIcon icon={faRotate} /> Conciliar repasses</button><button disabled={!destination|| balance < 100|| risk.payoutsBlocked} title={risk.payoutsBlocked ? "Regularize o saldo devedor antes de solicitar um novo repasse.": "Solicitar transferência"} onClick={() => openModal("payout")}><FontAwesomeIcon icon={faMoneyBillTransfer} /> Solicitar transferência</button></div>
    </section>
    <section className="fxpay-card fxpay-table-card"><header><div><small>REPASSES</small><h3>Histórico de transferências</h3></div></header><div className="fxpay-table-wrap"><table className="fxpay-table"><thead><tr><th>Código</th><th>Solicitado em</th><th>Destino</th><th>Tarifa</th><th>Valor</th><th>Status</th><th>Conciliação</th><th>Ações</th></tr></thead><tbody>{payouts.length === 0 ? <tr><td colSpan="8" className="fxpo-empty">Nenhuma transferência solicitada.</td></tr> : payouts.map((row) => <tr key={row.uuid}><td><b>{row.uuid.slice(0, 8)}</b></td><td>{dateTime(row.created_at)}</td><td>{row.destination_masked || "Pix CNPJ"}</td><td>{money(row.fee_cents / 100)}</td><td><b>{money(row.amount_cents / 100)}</b></td><td><span className={`fxpo-badge is-${row.status}`}>{fluxopayStatusLabel(row.status)}</span></td><td><span className={`fxpo-badge is-${row.reconciliation_status}`}>{fluxopayStatusLabel(row.reconciliation_status)}</span></td><td><div className="fxpo-row-actions"><button disabled={busyRow === row.uuid || !row.provider_payout_id} onClick={() => sync(row)} title="Consultar no Asaas"><FontAwesomeIcon icon={faRotate} /></button><button disabled={busyRow === row.uuid || !row.receipt_url} onClick={() => receipt(row)} title="Abrir comprovante"><FontAwesomeIcon icon={faFileArrowDown} /></button>
            <button
              disabled={busyRow === row.uuid}
              onClick={() => setAuditPayout(row)}
              title="Ver linha do tempo"
            >
        <FontAwesomeIcon icon={faClockRotateLeft} />
      </button>
    </div></td></tr>)}</tbody></table></div></section>
    {modal && 
      <div className="fxpo-backdrop" onMouseDown={closeModal}>
        <form className="fxpo-modal" onMouseDown={(event) => event.stopPropagation()} onSubmit={modal === "destination" ? saveDestination : requestPayout}>
          <header>
            <div>
              <small>{modal === "destination" ? "DESTINO DO REPASSE" : "TRANSFERÊNCIA PIX"}</small>
              <h3>{modal === "destination" ? "Chave Pix da clínica" : "Solicitar transferência"}</h3>
            </div>
            <button type="button" disabled={busy} onClick={closeModal}><FontAwesomeIcon icon={faXmark} /></button>
          </header>
          {modal === "destination" ? 
          <><label>CNPJ utilizado como chave Pix<input value={pixKey} onChange={(event) => setPixKey(event.target.value)} placeholder="00.000.000/0000-00" required /></label>
          <p className="fxpo-notice">A chave precisa ser o mesmo CNPJ cadastrado para a entidade.</p></> : 
          <><div className="fxpo-summary">
            <span>Saldo disponível <b>{money(balance / 100)}</b></span>
            <span>Destino <b>{destination?.display_mask}</b></span>
          </div>
          <label>Valor a transferir<input type="number" min="1" max={balance / 100} step="0.01" value={amount} onChange={(event) => setAmount(event.target.value)} required /></label></>}
          <label>Confirme sua senha atual<input type="password" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} required /></label>
          {modal === "payout" && 
          <label className="fxpo-confirm">
            <input type="checkbox" checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} required />
            <span>Confirmo o valor e a transferência para a chave Pix CNPJ da clínica.</span>
          </label>}
          <footer>
            <button type="button" className="fxpo-secondary" disabled={busy} onClick={closeModal}>Cancelar</button>
            <button type="submit" disabled={busy || !password || (modal === "payout" && !confirmed)}>
              {busy ? "Processando..." : modal === "destination" ? "Salvar chave Pix" : "Confirmar transferência"}</button>
          </footer>
        </form>
        {auditPayout && (
          <div
            className="fxpo-backdrop"
            onMouseDown={() => setAuditPayout(null)}
          >
            <div
              className="fxpo-modal"
              onMouseDown={(event) => event.stopPropagation()}
            >
              <header>
                <div>
                  <small>AUDITORIA FINANCEIRA</small>
                  <h3>
                    Repasse {auditPayout.uuid.slice(0, 8)}
                  </h3>
                </div>

                <button
                  type="button"
                  onClick={() => setAuditPayout(null)}
                >
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              </header>

              <PayoutAuditTimeline
                clinicPayoutUuid={auditPayout.uuid}
              />
            </div>
          </div>
        )}
      </div>}
  </div>;
}
